import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, ChevronRight, CheckCircle, TrendingUp, Fuel, Factory, Wrench, Shield, DollarSign } from 'lucide-react';
import { useLanguage } from '../contexts/languageStore';
import { useWebsite } from '../contexts/websiteStore';

export function Services() {
    const ref = useRef<HTMLDivElement>(null);
    const isInView = useInView(ref, { once: true, margin: "-50px" });
    const { t, isRTL } = useLanguage();
    const { config, getAsset } = useWebsite();
    const [openIndex, setOpenIndex] = useState<number | null>(null);

    const scrollToPortfolio = () => {
        const portfolioSection = document.getElementById('portfolio');
        portfolioSection?.scrollIntoView({ behavior: 'smooth' });
    };

    const services = (() => {
        const baseServices = [
            {
                icon: Fuel,
                title: t("services.service1"),
                description: t("services.CrudeOilTradingDescription"),
                features: [
                    t("services.onshore_offshore"),
                    t("services.producer_connections"),
                    t("services.logistics_support")
                ],
                image: getAsset(config.assets.service1 || 'service1.webp'),
                stats: ""
            },
            {
                icon: Factory,
                title: t("services.service2"),
                description: t("services.RefinedProductsTradingDescription"),
                features: [
                    t("services.diesel_gasoline"),
                    t("services.reliable_supply"),
                    t("services.industrial_clients")
                ],
                image: getAsset(config.assets.service2 || 'service2.webp'),
                stats: ""
            },
            {
                icon: TrendingUp,
                title: t("services.service3"),
                description: t("services.FlancoatVGODescription"),
                features: [
                    t("services.bulk_supply"),
                    t("services.industrial_clients"),
                    t("services.reliable_supply")
                ],
                image: getAsset(config.assets.service3 || 'service3.webp'),
                stats: ""
            },
            {
                icon: Wrench,
                title: t("services.service4"),
                description: t("services.OilfieldEquipmentDescription"),
                features: [
                    t("services.parts_tools"),
                    t("services.operational_support"),
                    t("services.citex_emcor")
                ],
                image: getAsset(config.assets.service4 || 'service4.webp'),
                stats: ""
            }
        ];

        // Add EMCOR-specific services (5 and 6)
        if (config.name === "EMCOR") {
            baseServices.push(
                {
                    icon: Shield,
                    title: t("services.service5"),
                    description: t("services.EnvironmentalStewardshipDescription"),
                    features: [
                        t("services.environmental_care"),
                        t("services.ecological_impact"),
                        t("services.energy_efficiency"),
                        t("services.compliance_standards")
                    ],
                    image: getAsset(config.assets.service5 || 'service5.webp'),
                    stats: t("services.environmental_focus")
                },
                {
                    icon: DollarSign,
                    title: t("services.service6"),
                    description: t("services.InvestmentDescription"),
                    features: [
                        t("services.oil_gas_investment"),
                        t("services.renewable_energy"),
                        t("services.sustainable_future"),
                        t("services.global_transition")
                    ],
                    image: getAsset(config.assets.service6 || 'service6.webp'),
                    stats: t("services.investment_focus")
                }
            );
        }

        return baseServices;
    })();


    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 }
    };

    return (
        <section id="services" className="relative overflow-hidden pt-24">
            {/* Interactive gradient background */}
            <motion.div
                className="absolute inset-0"
                animate={{
                    background: [
                        `radial-gradient(circle at 50% 50%, var(--primary-color-rgb, 59, 130, 246) / 0.15 0%, transparent 40%)`,
                        `radial-gradient(circle at 50% 50%, var(--secondary-color-rgb, 34, 197, 94) / 0.15 0%, transparent 40%)`,
                        `radial-gradient(circle at 50% 50%, var(--primary-color-rgb, 59, 130, 246) / 0.15 0%, transparent 40%)`
                    ]
                }}
                transition={{
                    duration: 0.8,
                    ease: "easeOut"
                }}
            />

            {/* Enhanced background with vibrant gradients */}
            <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-primary/5 to-secondary/5"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-primary/3 via-transparent to-secondary/3"></div>

            {/* Animated background elements */}
            <motion.div
                className="absolute top-0 left-0 w-full h-full"
                animate={{
                    background: [
                        `radial-gradient(circle at 20% 80%, var(--primary-color-rgb, 59, 130, 246) / 0.1 0%, transparent 50%)`,
                        `radial-gradient(circle at 80% 20%, var(--secondary-color-rgb, 34, 197, 94) / 0.1 0%, transparent 50%)`,
                        `radial-gradient(circle at 20% 80%, var(--primary-color-rgb, 59, 130, 246) / 0.1 0%, transparent 50%)`
                    ]
                }}
                transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: "easeInOut"
                }}
            />

            <div className="container mx-auto px-6 relative z-10 max-w-7xl">
                <motion.div
                    ref={ref}
                    initial={{ opacity: 0, y: 50 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-16"
                >
                    <motion.div
                        className="mt-10 inline-flex items-center px-8 py-4 bg-primary/5 text-primary border border-primary/10 text-sm font-light tracking-wider mb-8"
                        initial={{ opacity: 0, y: 20 }}
                        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                        transition={{ delay: 0.2, duration: 0.8 }}
                    >
                        <span className="w-px h-4 bg-gray-800 mr-4"></span>
                        <span className="uppercase tracking-[0.2em]">{t("services.our_services")}</span>
                        <span className="w-px h-4 bg-gray-800 ml-4"></span>
                    </motion.div>
                    <motion.h2
                        className="text-5xl md:text-6xl font-light text-gray-900 mb-8 leading-tight"
                        initial={{ opacity: 0, y: 30 }}
                        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                        transition={{ delay: 0.3, duration: 1.2, ease: "easeOut" }}
                    >
                        {t("services.our_core_services")}
                    </motion.h2>
                    <motion.p
                        className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed tracking-wide"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5, duration: 1.2, ease: 'easeOut' }}
                    >
                        {t("services.Services_Description")}
                    </motion.p>
                </motion.div>

                {/* Main Services Grid */}
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                    className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-7xl mx-auto mb-16"
                >
                    {services.map((service, index) => (
                        <motion.div
                            key={index}
                            variants={itemVariants}
                            whileHover={{ y: -4 }}
                            transition={{ duration: 0.6, ease: "easeOut" }}
                            className="group"
                        >
                            <div className="h-full border border-gray-200/20 hover:border-primary/20 transition-all duration-700 bg-gradient-to-br from-white/90 via-white/80 to-primary/5 backdrop-blur-sm overflow-hidden relative rounded-lg">
                                {/* Enhanced background gradient */}
                                <motion.div
                                    className="absolute inset-0 bg-gradient-to-br from-primary/10 via-secondary/5 to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                                    initial={false}
                                />

                                {/* Service Image with enhanced overlay */}
                                <div className="relative h-56 overflow-hidden">
                                    <motion.img
                                        src={service.image}
                                        alt={service.title}
                                        className="w-full h-full object-cover"
                                        whileHover={{ scale: 1.1 }}
                                        transition={{ duration: 0.6 }}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>

                                    {/* Floating icon */}
                                    <motion.div
                                        className="absolute top-6 left-6"
                                        whileHover={{ y: -2 }}
                                        transition={{ duration: 0.4 }}
                                    >
                                        <div className="w-12 h-12 bg-white/10 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/20 transition-all duration-500 border border-white/10">
                                            <service.icon className="h-6 w-6 text-white" />
                                        </div>
                                    </motion.div>

                                    {/* Stats badge */}
                                    <motion.div
                                        className="absolute top-6 right-6 px-3 py-1 bg-secondary/90 text-white text-xs font-medium rounded-full"
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: 0.3, duration: 0.5 }}
                                    >
                                        {service.stats}
                                    </motion.div>
                                </div>

                                <div className="p-6 relative">
                                    <div className="flex items-start justify-between mb-4">
                                        <h3 className="text-3xl font-light text-gray-900 group-hover:text-primary transition-colors duration-300">
                                            {service.title}
                                        </h3>
                                        <motion.div
                                            whileHover={{ x: 2 }}
                                            transition={{ duration: 0.4 }}
                                        >
                                            <ChevronRight className={`h-5 w-5 text-gray-500 group-hover:text-primary transition-colors duration-500 ${isRTL ? 'rotate-180' : ''}`} />
                                        </motion.div>
                                    </div>

                                    <p className="text-base text-gray-600 mb-4">
                                        {service.features.slice(0, 1)}
                                    </p>
                                    <ul className="space-y-2 mb-4">
                                        {service.features.slice(0, 3).map((feature, i) => (
                                            <li key={i} className="flex items-center text-sm text-gray-700">
                                                <CheckCircle className={`h-4 w-4 text-primary ${isRTL ? 'ml-2' : 'mr-2'}`} />
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded text-sm transition-colors duration-300"
                                        onClick={() => setOpenIndex(openIndex === index ? null : index)}
                                    >
                                        {openIndex === index ? t('services.show_less') : t('services.learn_more')}
                                    </button>
                                    {openIndex === index && (
                                        <div className="mt-4 text-sm text-gray-600 whitespace-pre-line">
                                            {service.description}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>

                {/* CTA Section - Partner with Us */}
                <div className="relative flex justify-center items-center py-20">
                    {/* Subtle Animated Background */}
                    <motion.div
                        className="absolute inset-0 flex justify-center items-center pointer-events-none z-0"
                        initial="hidden"
                        animate="visible"
                        variants={{
                            hidden: {},
                            visible: {
                                transition: { staggerChildren: 0.3 }
                            }
                        }}
                    >
                        {[
                            { width: 85, height: 95, left: 23, top: 59, scale: 0.9, opacity: 0.37, y: -20 },
                            { width: 78, height: 83, left: 72, top: 23, scale: 1.05, opacity: 0.45, y: -25 },
                            { width: 64, height: 64, left: 68, top: 57, scale: 1.02, opacity: 0.38, y: -18 },
                            { width: 77, height: 97, left: 70, top: 30, scale: 0.96, opacity: 0.31, y: -22 }
                        ].map((props, i) => (
                            <motion.div
                                key={i}
                                className="absolute rounded-full bg-secondary/20 blur-xl opacity-40"
                                style={{
                                    width: `${props.width}px`,
                                    height: `${props.height}px`,
                                    left: `${props.left}%`,
                                    top: `${props.top}%`,
                                    zIndex: 0,
                                }}
                                initial={{
                                    scale: props.scale,
                                    opacity: props.opacity,
                                    y: 0,
                                }}
                                animate={{
                                    y: [0, props.y, 0],
                                    opacity: [props.opacity, props.opacity + 0.2, props.opacity],
                                    scale: [1, props.scale + 0.05, 1],
                                }}
                                transition={{
                                    duration: 5 + i * 0.5,
                                    repeat: Infinity,
                                    repeatType: "loop",
                                    delay: i * 0.8,
                                    ease: "easeInOut",
                                }}
                            />
                        ))}
                    </motion.div>

                    {/* Blended Card */}
                    <motion.div
                        className="relative z-10 backdrop-blur-sm bg-white/20 rounded-xl px-4 sm:px-8 md:px-12 py-12 max-w-2xl w-full text-center border border-white/10"
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        whileInView={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{ duration: 0.7, type: "spring" }}
                        whileHover={{ scale: 1.01 }}
                    >
                        <motion.h3
                            className="text-3xl md:text-4xl font-light mb-4 leading-tight text-gray-900 tracking-wide"
                            initial={{ opacity: 0, y: 15 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1, duration: 0.6 }}
                        >
                            {t("services.ready_to_partner_in")} <span className="text-gray-900 font-light text-3xl md:text-4xl tracking-wide">{t("services.energy_commodities")} {isRTL ? '؟' : '?'}</span>
                        </motion.h3>
                        <motion.p
                            className="text-gray-600 mb-6 leading-relaxed max-w-md mx-auto text-lg"
                            initial={{ opacity: 0, y: 15 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2, duration: 0.6 }}
                        >
                            {t("services.lets")} <span className="text-gray-900">{t("services.energy_commodities")}</span> {t("services.trading")}.
                        </motion.p>
                        <motion.div
                            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
                            initial={{ opacity: 0, y: 15 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3, duration: 0.6 }}
                        >
                            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }}>
                                <button className="bg-primary hover:bg-primary/90 text-white hover:text-white px-6 py-3 rounded-lg font-medium text-base transition-all duration-300 shadow-sm inline-flex items-center">
                                    {t("services.contact_our_team")}
                                    <ArrowRight className={`${isRTL ? 'mr-2 rotate-180' : 'ml-2'} h-5 w-5`} />
                                </button>
                            </motion.div>
                            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }}>
                                <button
                                    onClick={scrollToPortfolio}
                                    className="border border-primary/30 text-primary hover:bg-primary/5 px-6 py-3 rounded-lg font-medium text-base transition-all duration-300 inline-flex items-center"
                                >
                                    {t("services.view_facilities")}
                                    <ArrowRight className={`${isRTL ? 'mr-2 rotate-180' : 'ml-2'} h-5 w-5`} />
                                </button>
                            </motion.div>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
